import { useEffect, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import {
  fileContentUrl,
  listClaimFiles,
  listInvoicePhotos,
  setInvoicePhotos,
} from "../../services/apiClient.js";

// Choose the finished photographs that go out with an invoice (Vero's request,
// docs/invoice-refactor.md — GPS-CD-004). What Vero want is proof the work is
// done, so the source is the job's CONTRACTOR PHOTOS, the same section the Files
// tab shows.
//
// A modal of its own rather than a second mode bolted onto ZipAllPhotos: that
// component is an archive builder living in an experiments folder, and the two
// only look alike. Sharing it would couple a billing document to an experiment.
//
// Selection belongs to the INVOICE, like inspection photos belong to their
// inspection — 18% of claims carry more than one invoice, so a shared set would
// make a reprint of the first show the second one's pictures. "Copy from the
// previous invoice" is the shortcut for that case.

// ONE key type throughout: postgres returns bigint as a STRING, so a selection
// loaded from the server arrived as one type and a selection made by clicking
// arrived as another. `indexOf` then matched only the tiles clicked in this
// session, and photographs already saved against the invoice showed no badge at
// all — the count said "6 selected" while one tile looked picked (Robert,
// 14 Aug). Everything here is a string; the server coerces on the way in.
const key = (v) => String(v);

const PICKABLE = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "jfif"]);
const ext = (f) => String(f.extension || "").toLowerCase();

// HEIC is deliberately shown but NOT selectable. Browsers cannot decode it, so a
// selectable one is a broken tile on screen and a blank on the printed invoice;
// hiding them entirely produces "where did that photo go". Naming it and
// refusing the click is the only version that explains itself.
const unsupported = (f) => f.is_image && !PICKABLE.has(ext(f));

function InvoicePhotoPicker({ claimId, invoice, previousInvoiceId, onClose, onSaved }) {
  // A sent invoice is READ-ONLY here, and that has to be visible in the tiles,
  // not just in a disabled Save button. The first version let you pick a set on
  // a sent invoice and only refused at the end — work that could never be saved
  // (found by Robert, 14 Aug, on an imported invoice: every one of the ~4,900
  // carried over from 1.0 arrives sent).
  const locked = !!invoice.sent_at;
  const [files, setFiles] = useState([]);
  const [chosen, setChosen] = useState([]); // ordered file ids
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    Promise.all([listClaimFiles(claimId), listInvoicePhotos(claimId, invoice.id)])
      .then(([all, picked]) => {
        if (ac.signal.aborted) return;
        setFiles(all.filter((f) => f.file_type === "Contractor Photos" && f.is_image));
        setChosen(picked.map((p) => key(p.id)));
      })
      .catch(() => !ac.signal.aborted && setError("Unable to load the job's photographs."))
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [claimId, invoice.id]);

  // Order is the order they were picked, which is the order they print.
  function toggle(f) {
    if (locked || unsupported(f)) return;
    const k = key(f.id);
    setChosen((cur) => (cur.includes(k) ? cur.filter((id) => id !== k) : [...cur, k]));
  }

  async function copyPrevious() {
    setError("");
    try {
      const prev = await listInvoicePhotos(claimId, previousInvoiceId);
      const here = new Set(files.map((f) => key(f.id)));
      setChosen(prev.map((p) => key(p.id)).filter((id) => here.has(id)));
    } catch {
      setError("Unable to copy from the previous invoice.");
    }
  }

  async function save() {
    setBusy(true);
    setError("");
    try {
      await setInvoicePhotos(claimId, invoice.id, chosen);
      onSaved?.();
      onClose();
    } catch (err) {
      // The server refuses a sent invoice, non-Contractor-Photos and HEIC. Its
      // sentence is better than anything invented here.
      setError(err.response?.data?.error || "Unable to save the selection.");
      setBusy(false);
    }
  }

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Photographs for invoice #{invoice.invoice_no}
          <span className="text-muted fs-6 ms-2">{chosen.length} selected</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {invoice.sent_at ? (
          <Alert variant="warning">
            This invoice has been sent, so its photographs are locked and cannot be
            changed here. Close this, click <strong>Reopen</strong> on the invoice
            row, then choose photographs and mark it as sent again.
          </Alert>
        ) : null}
        {loading ? (
          <div className="text-center text-muted py-4"><Spinner size="sm" animation="border" /> Loading…</div>
        ) : files.length === 0 ? (
          <div className="text-muted py-3">
            This job has no contractor photographs. They are uploaded on the Files tab under
            &ldquo;Contractor Photos&rdquo;.
          </div>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            {files.map((f) => {
              const pos = chosen.indexOf(key(f.id));
              const bad = unsupported(f);
              return (
                <div
                  key={f.id}
                  role="button"
                  onClick={() => toggle(f)}
                  data-hint={bad ? `${f.original_filename} — ${ext(f).toUpperCase()} can't be shown by a browser, so it can't go on an invoice` : f.original_filename}
                  className="position-relative border rounded p-1"
                  style={{
                    width: 132,
                    cursor: bad || locked ? "not-allowed" : "pointer",
                    opacity: bad ? 0.45 : 1,
                    // Same selected treatment as the Files tab's zip picker
                    // (photoHelix.css .ph-tile-selected) — 3px #2e9e4f inset —
                    // so "picked" looks identical wherever you are picking
                    // things (Robert, 14 Aug: "keep it consistent").
                    outline: pos >= 0 ? "3px solid #2e9e4f" : undefined,
                    outlineOffset: pos >= 0 ? "-3px" : undefined,
                  }}
                >
                  {bad ? (
                    <div className="d-flex align-items-center justify-content-center bg-body-tertiary text-muted small text-center"
                      style={{ width: "100%", height: 92 }}>
                      {ext(f).toUpperCase()}<br />not supported
                    </div>
                  ) : (
                    // THUMBNAIL, not the original. These tiles are 132px wide and
                    // the originals average 275 kB, so a job with twenty photos
                    // was pulling ~5.5 MB to draw them — which is what Karen felt
                    // as the picker being slow (16 Aug). Every other image surface
                    // in the app already does this (FileCard, ZipAllPhotos, the
                    // inspection page); this one was the omission. Falls back to
                    // the original when no thumbnail exists, which is the case for
                    // any file whose bytes have not been backfilled yet.
                    <img src={fileContentUrl(f.thumbnail_file_id || f.id)} alt=""
                      style={{ width: "100%", height: 92, objectFit: "cover" }} />
                  )}
                  <div className="small text-truncate mt-1" style={{ fontSize: "0.7rem" }}>
                    {f.original_filename}
                  </div>
                  {/* The green circle sits top-RIGHT and is the same size and
                      colour as the zip picker's tick — but carries the ORDER
                      instead, because these photographs print in the order they
                      were chosen and the archive's do not. One indicator doing
                      both jobs: a tick beside a number says nothing the number
                      has not already said. */}
                  {pos >= 0 ? (
                    <span
                      className="position-absolute d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        top: 6, right: 6, width: 21, height: 21,
                        background: "#2e9e4f", color: "#fff",
                        borderRadius: "50%", fontSize: 12, lineHeight: 1,
                        boxShadow: "0 1px 4px rgba(24, 60, 36, 0.35)",
                      }}
                      data-hint={`Prints ${pos + 1}${pos === 0 ? "st" : pos === 1 ? "nd" : pos === 2 ? "rd" : "th"}`}
                    >
                      {pos + 1}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {previousInvoiceId && !invoice.sent_at ? (
            <Button variant="outline-secondary" size="sm" onClick={copyPrevious} disabled={busy}>
              Copy from previous invoice
            </Button>
          ) : null}
        </div>
        <div>
          <Button variant="secondary" size="sm" className="me-2" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy || locked}>
            {busy ? "Saving…" : "Save selection"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoicePhotoPicker;
